import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ROUTE } from 'src/constants/routes';
import { getAccountData, getBrokerAccountData } from 'src/store/broker';
import {
  getIssuerAccountMembers,
  getIssuerAccountProposalData,
  getIssuerCoveragePlans,
  getIssuerDataSelector,
  isLoadingIssuerCoveragePlansSelector,
  issuerCoveragePlansSelector,
} from 'src/store/issuer';
import {
  chatRoomsSelector,
  getChatRooms,
  isLoadingStartChatSelector,
  startChat,
} from 'src/store/chat';

import { MenuSubItem } from '../MenuISubtem/MenuISubtem';
import { MenuItem } from '../MenuItem/MenuItem';

import classes from './repMenu.module.scss';

export const RepMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();

  const issuerData = useSelector(getIssuerDataSelector);
  const accountData = useSelector(getAccountData);
  const proposalData = useSelector(getIssuerAccountProposalData);
  const coveragePlans = useSelector(issuerCoveragePlansSelector);
  const chatRooms = useSelector(chatRoomsSelector);
  const isLoadingStartChat = useSelector(isLoadingStartChatSelector);
  const isLoadingCoveragePlans = useSelector(isLoadingIssuerCoveragePlansSelector);

  const linesOfCoverage = searchParams.get('lines_of_coverage')?.split(',') || [];

  const accountId = params?.id;
  const proposalId = params?.proposalId;

  const isMyRfps =
    pathname?.includes('accounts') &&
    pathname?.includes('proposals') &&
    pathname?.includes('my-rfps');

  const isMyQuotes =
    pathname?.includes('accounts') &&
    pathname?.includes('proposals') &&
    pathname?.includes('my-quotes');

  const isSettings = pathname?.includes('settings');
  const isActiveQuotes =
    pathname?.includes('accounts') &&
    pathname?.includes('proposals') &&
    pathname?.includes('quotes');

  const activeTab = searchParams.get('activeTab');

  useEffect(() => {
    if (accountId) {
      dispatch(getBrokerAccountData({ paramsId: accountId }));
    }
  }, [dispatch, accountId]);

  useEffect(() => {
    if (accountId && proposalId) {
      dispatch(getIssuerAccountMembers({ accountId, proposalId, navigate }));

      const payload = {
        'f[proposal_id]': proposalId,
        'f[room_type]': 'carrier',
        'f[user_id]': issuerData.id,
      };
      dispatch(getChatRooms(payload));
    }
  }, [dispatch, navigate, accountId, proposalId, issuerData]);

  useEffect(() => {
    if (accountId && proposalId && chatRooms?.length) {
      const period = setInterval(() => {
        let payload = {
          'f[proposal_id]': proposalId,
          'f[room_type]': 'carrier',
          'f[user_id]': issuerData.id,
        };

        if (accountId && proposalId) {
          dispatch(getChatRooms(payload));
        }
      }, 10000);

      return () => {
        clearInterval(period);
      };
    }
  }, [dispatch, accountId, proposalId, chatRooms, issuerData]);

  useEffect(() => {
    if (accountId && proposalId && isMyRfps) {
      dispatch(getIssuerCoveragePlans({ accountId, proposalId }));
    }
  }, [dispatch, accountId, proposalId, isMyRfps]);

  const getPreserved = useCallback(() => {
    const p = new URLSearchParams(search);

    p.delete('activeTab');

    return p.toString();
  }, [search]);

  const withPreserved = useCallback(() => {
    const s = getPreserved();

    return s ? `?${s}` : '';
  }, [getPreserved]);

  const withActiveTab = useCallback(
    (tab) => {
      const p = new URLSearchParams(getPreserved());

      p.set('activeTab', tab);

      return `?${p.toString()}`;
    },
    [getPreserved],
  );

  const updatedPathname = `${pathname}${search ? search : ''}`;

  const searchValue = search || '';

  const isVisibleProposalMenu = useCallback(() => {
    switch (updatedPathname) {
      case `/rep/my-rfps/accounts/${accountData?.id}/proposals/${proposalData?.id}${searchValue}`:
      case `/rep/my-rfps/accounts/${accountData?.id}/proposals/${proposalData?.id}/account-info${searchValue}`:
      case `/rep/my-rfps/accounts/${accountData?.id}/proposals/${proposalData?.id}/census${searchValue}`:
      case `/rep/my-rfps/accounts/${accountData?.id}/proposals/${proposalData?.id}/quotes${searchValue}`:
      case `/rep/my-rfps/accounts/${accountData?.id}/proposals/${proposalData?.id}/chat${searchValue}`:
      case `/rep/my-rfps/accounts/${accountData?.id}/proposals/${proposalData?.id}/finalize${searchValue}`:
      case `/rep/my-quotes/accounts/${accountData?.id}/proposals/${proposalData?.id}${searchValue}`:
      case `/rep/my-quotes/accounts/${accountData?.id}/proposals/${proposalData?.id}/account-info${searchValue}`:
      case `/rep/my-quotes/accounts/${accountData?.id}/proposals/${proposalData?.id}/census${searchValue}`:
      case `/rep/my-quotes/accounts/${accountData?.id}/proposals/${proposalData?.id}/quotes${searchValue}`:
      case `/rep/my-quotes/accounts/${accountData?.id}/proposals/${proposalData?.id}/chat${searchValue}`:
      case `/rep/my-quotes/accounts/${accountData?.id}/proposals/${proposalData?.id}/finalize${searchValue}`:
        return true;
      default:
        return false;
    }
  }, [updatedPathname, accountData, proposalData, searchValue]);

  const onClickQuotes = useCallback(() => {
    const tab = isMyQuotes ? 'pet' : coveragePlans?.active_tab;

    navigate(
      `/rep/my-rfps/accounts/${accountData?.id}/proposals/${proposalData?.id}/quotes${withActiveTab(
        tab,
      )}`,
    );
  }, [
    isMyQuotes,
    coveragePlans?.active_tab,
    navigate,
    accountData?.id,
    proposalData?.id,
    withActiveTab,
  ]);

  const onClickSettings = useCallback(() => {
    navigate(`${ROUTE.ISSUER_SETTINGS}?activeTab=profile`);
  }, [navigate]);

  const onClickStartChat = useCallback(() => {
    const payload = {
      owner_id: issuerData.id,
      owner_type: 'rep',
      room_type: 'carrier',
      proposal_id: proposalId,
      name: proposalData.name,
    };

    const accountContacts = accountData.contacts.filter((item) => item.contact_type === 'broker');

    dispatch(
      startChat({
        accountId,
        contacts: [{ contact_type: 'rep', id: issuerData.id }, ...accountContacts],
        payload,
        navigate,
      }),
    );
  }, [dispatch, navigate, proposalId, accountData, accountId, issuerData, proposalData]);

  const normalizedRouteType = !isMyQuotes ? 'my-rfps' : 'my-quotes';

  return (
    <div className={classes.RepMenu}>
      <MenuItem title="My RFPs" routeTo={ROUTE.REP_MY_RFPS} isActive={isMyRfps} />
      <MenuItem title="My Quotes" routeTo={ROUTE.REP_MY_QUOTES} isActive={isMyQuotes} />
      <MenuItem title="Browse RFPs" subTitle="Free Beta" routeTo={ROUTE.REP_BROWSE_BROKERS} />
      <MenuItem title="Settings" onClick={onClickSettings} isLink={false} />
      {isSettings && (
        <MenuSubItem
          title="Profile"
          routeTo="?activeTab=profile"
          isActive={activeTab === 'profile'}
        />
      )}
      {isVisibleProposalMenu() && (
        <>
          <hr className={classes.RepMenuLine} />
          <MenuItem
            title="Account Info"
            routeTo={`/rep/${normalizedRouteType}/accounts/${accountData?.id}/proposals/${
              proposalData?.id
            }/account-info${withPreserved()}`}
          />
          <MenuItem
            title="Census"
            routeTo={`/rep/${normalizedRouteType}/accounts/${accountData?.id}/proposals/${
              proposalData?.id
            }/census${withPreserved()}`}
          />
          <MenuItem
            title="Quotes"
            isDisabled={isLoadingCoveragePlans}
            isLoading={isLoadingCoveragePlans}
            onClick={onClickQuotes}
            isLink={false}
          />
          {isActiveQuotes && !isMyQuotes && (
            <>
              {Object.keys(coveragePlans)?.map(
                (item) =>
                  item !== 'active_tab' && (
                    <MenuSubItem
                      title={item?.replace('_', ' ')}
                      routeTo={`${withActiveTab(item)}`}
                      isActive={activeTab === item}
                      key={item}
                    />
                  ),
              )}
            </>
          )}
          {isActiveQuotes && isMyQuotes && (
            <>
              {linesOfCoverage?.includes('pet') ? (
                <MenuSubItem
                  title="Pet"
                  routeTo={`/rep/my-quotes/accounts/${accountData?.id}/proposals/${
                    proposalData?.id
                  }/quotes${withActiveTab('pet')}`}
                  isActive={activeTab === 'pet'}
                />
              ) : null}
              {/* {linesOfCoverage?.includes('decision_support') ? (
                <MenuSubItem
                  title="Decision Support"
                  routeTo={`/rep/my-quotes/accounts/${accountData?.id}/proposals/${
                    proposalData?.id
                  }/quotes${withActiveTab('decision_support')}`}
                  isActive={activeTab === 'decision_support'}
                />
              ) : null} */}
            </>
          )}
          <MenuItem
            title="Final Proposal"
            routeTo={`/rep/${normalizedRouteType}/accounts/${accountData?.id}/proposals/${
              proposalData?.id
            }/finalize${withPreserved()}`}
          />
          {chatRooms?.length ? (
            <MenuItem
              title="Chat"
              subTitle="New"
              routeTo={`/rep/${normalizedRouteType}/accounts/${accountData?.id}/proposals/${
                proposalData?.id
              }/chat${withPreserved()}`}
            />
          ) : (
            <MenuItem
              title="Start Chat"
              subTitle="New"
              onClick={onClickStartChat}
              isLoading={isLoadingStartChat}
              isLink={false}
            />
          )}
        </>
      )}
    </div>
  );
};
